import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { Button } from 'components/buttons/button';
import { ButtonType, CircleButtonSize } from 'components/buttons/button-types';
import { CircleActionButton } from 'components/buttons/circle-action-button/circle-action-button';
import { Message } from 'core/models/live-chat';

const LiveChatScreen = observer(
  ({
    styles,
    messages = [],
    latestEvent,
    isConnected,
    handleFeedback,
    sendMessage,
  }: {
    styles: { [key: string]: string };
    messages: Message[];
    latestEvent?: Message;
    isConnected: boolean;
    handleFeedback: () => void;
    sendMessage: (value: string) => void;
  }) => {
    const messageRef: any = useRef();
    const { t } = useTranslation();

    useEffect(() => {
      if (messageRef && messageRef.current) {
        const { scrollHeight, clientHeight } = messageRef.current;
        messageRef.current.scrollTo({ left: 0, top: scrollHeight - clientHeight, behavior: 'smooth' });
      }
    }, [messages]);

    const [inputMessage, setInputMessage] = useState<string>('');

    const handleClick = (e: any) => {
      e.preventDefault();
      if (inputMessage !== '') {
        sendMessage(inputMessage);
        setInputMessage('');
      }
    };

    const getMessageClass = (message: Message) => {
      if (message.isIncomingMessage) {
        return styles.primaryChatBlock;
      }

      return styles.secondaryChatBlock;
    };

    return (
      <div ref={messageRef} className={styles.liveChatScreen}>
        <div className="cust-pt-2">
          {messages.map((m: any) => {
            return (
              <div className={`d-flex justify-between ${getMessageClass(m)} ${styles.chatBlock}`}>
                <div className={styles.chatBody}>{m.Message}</div>
                {m.Timestamp !== null && (
                  <div className={styles.timeStamp}>
                    <small>{moment(m.Timestamp).format('HH:mm')}</small>
                  </div>
                )}
              </div>
            );
          })}
          {latestEvent && (
            <div className={`d-flex justify-between ${styles.eventChatBlock} ${styles.chatBlock}`}>
              <div className={styles.chatBody}>{latestEvent.Message}</div>
            </div>
          )}
        </div>

        <div className={`d-flex justify-content-between ${styles.inputBlock}`}>
          <input
            className={`form-control ${styles.inputStyle}`}
            placeholder={t('liveChat.placeholderText')}
            value={inputMessage}
            maxLength={255}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyUp={(event) => {
              if (event.key === 'Enter') {
                handleClick(event);
              }
            }}
          />
          <span>
            <CircleActionButton
              meta={{
                type: ButtonType.Primary,
                name: 'send',
                iconName: 'icon-Arrow_right',
                size: CircleButtonSize.Small,
              }}
              classes={['cust-ml-2']}
              onClick={handleClick}
            ></CircleActionButton>
          </span>
        </div>

        {!isConnected && (
          <>
            <div className="d-flex justify-content-center">
              <Button
                meta={{
                  type: ButtonType.Disabled,
                  name: t('liveChat.disconnect.sendButton'),
                  title: t('liveChat.disconnect.buttonTitle'),
                  iconName: 'icon-Arrow_right',
                }}
                classes={[styles.customButtonStyle]}
                onClick={() => {}}
              ></Button>
            </div>

            <div className={`text-center cust-px-4 cust-pb-4 ${styles.feedbackStyle}`}>
              <p className="no-margin cust-px-2 cust-mb-4">{t('liveChat.disconnect.title')}</p>
              <br />

              <p className="no-margin cust-px-2 cust-mb-4">
                {t('liveChat.disconnect.description')}{' '}
                <a className={styles.linkStyle} onClick={handleFeedback}>
                  click here
                </a>
              </p>
            </div>
          </>
        )}
      </div>
    );
  }
);
export default LiveChatScreen;